"use client";

import { Dispatch, SetStateAction, useContext, useEffect, useState } from "react";
import { ClientContentBlocks } from "@/components/common/content-blocks/client-content-blocks";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
// eslint-disable-next-line
import { Updater } from "use-immer";
import { Spinner } from "@/components/ui/spinner";
import { ResourceContext } from "@/contexts/resource-context";
import { logEvent } from "@/service/logs/log";
import { Quiz } from "@/components/common/quiz/quiz";
import { groupConsecutiveNonQuizBlocks } from "@/service/content-blocks/group-consecutive-non-quiz-blocks";
import { scrollToElementAdaptive } from "@/service/scroll/scroll";
import { ClinicalCaseInProgressStatus } from "@/components/clinical-case/clinical-case-show/components/clinical-case-in-progress/clinical-case-in-progress-status";
import { HorizontalPatron } from "@/components/video/horizontal-patron";

function hasQuiz(step: ClinicalCaseStep) {
  return step.content.some((block) => block.type === "quiz");
}

export const ClinicalCaseInProgress = ({
  patron,
  clinicalCase,
  onFinish,
  finished,
  currentStep,
  setCurrentStep,
}: {
  patron: PatronVM | null;
  clinicalCase: ClinicalCaseVM;
  onFinish: () => void;
  finished: boolean;
  currentStep: number;
  setCurrentStep: Dispatch<SetStateAction<number>>;
}) => {
  const { steps } = clinicalCase;
  const [canAdvance, setCanAdvance] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const { logView } = useContext(ResourceContext);

  useEffect(() => {
    if (!hasQuiz(clinicalCase.content.steps[currentStep])) {
      setCanAdvance(true);
    }
  }, [currentStep, clinicalCase, logView]);

  async function handleNext() {
    if (currentStep < steps - 1) {
      logEvent("clinical_case_next", logView, {
        from: currentStep,
        to: currentStep + 1,
        direction: "next",
      });
      setCurrentStep((prev) => {
        setTimeout(() => {
          scrollToElementAdaptive({
            selector: `#clinical-case-step-${prev + 1}`,
            offset: -140,
            behavior: "smooth",
          });
        }, 100);
        return prev + 1;
      });
    } else {
      setIsLoading(true);
      await onFinish();
      setIsLoading(false);
    }
  }

  return (
    <div
      className="mt-10 flex w-full flex-col items-center justify-center pb-24 md:pb-0"
      id={`clinical-case-in-progress`}
    >
      <ClinicalCaseInProgressStatus
        currentStep={currentStep}
        clinicalCase={clinicalCase}
      />

      <div className="w-full bg-white">
        {clinicalCase.content.steps
          .slice(0, currentStep + 1)
          .map((step, stepIndex) => (
            <div key={stepIndex} id={`clinical-case-step-${stepIndex}`}>
              <Separator className="my-10" />
              <div className="bg-remedium-blue/10 mb-2.5 flex h-6 w-6 items-center justify-center rounded-sm text-sm font-semibold text-black">
                {stepIndex + 1}
              </div>
              <h2 className="mb-2.5 text-xl font-semibold md:max-w-[80%]">
                {step.title}
              </h2>

              {groupConsecutiveNonQuizBlocks(step.content).map(
                (group, groupIndex) =>
                  group.type === "quiz" ? (
                    <Quiz
                      key={group.blocks[0].data.uuid}
                      quiz={group.blocks[0].data}
                      setCanAdvance={setCanAdvance}
                      stepIdx={stepIndex}
                      completedEvent={"clinical_case_step"}
                    />
                  ) : (
                    <ClientContentBlocks
                      key={`content-${stepIndex}`}
                      blocks={group.blocks}
                      theme="clinical_case"
                      fadeIn={true}
                    />
                  ),
              )}
            </div>
          ))}
        {!finished && (
          <div className="flex items-center justify-between bg-white px-4 py-4 xl:sticky xl:right-auto xl:left-auto xl:z-100 xl:my-8 xl:px-0 xl:shadow-none">
            {patron ? (
              <HorizontalPatron className="my-0" patron={patron} />
            ) : (
              <div></div>
            )}
            <Button
              variant={"blue"}
              className={"ml-auto min-w-[80px]"}
              disabled={!canAdvance || isLoading}
              onClick={handleNext}
            >
              {isLoading && <Spinner />}
              {currentStep < steps - 1 ? "Dalej" : "Zakończ wizytę"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
